import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Modal, ModalActions } from '../components/Modal';
import { PageSkeleton } from '../components/PageSkeleton';
import type { SubscriptionPlan, UsageMetrics } from '@agency-smm/shared';

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

// Current Plan Card
const CurrentPlanCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const PlanInfo = styled.div``;

const PlanName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const PlanPrice = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
`;

const PlanStatus = styled.span<{ $active: boolean }>`
  display: inline-block;
  padding: 2px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  background: ${({ $active }) => $active ? '#10B98120' : '#EF444420'};
  color: ${({ $active }) => $active ? '#10B981' : '#EF4444'};
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

// Usage Bars
const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const UsageCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const UsageLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const UsageValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ProgressBar = styled.div`
  height: 8px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number; $warning?: boolean }>`
  height: 100%;
  width: ${({ $percent }) => Math.min($percent, 100)}%;
  background: ${({ $warning }) => $warning ? '#EF4444' : '#3B82F6'};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: width 0.3s ease;
`;

// Plan Cards
const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const PlanCard = styled.div<{ $highlighted?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ $highlighted, theme }) => $highlighted ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  position: relative;
  display: flex;
  flex-direction: column;
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const PlanCardName = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const PlanCardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const PriceDisplay = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const PriceAmount = styled.span`
  font-size: 36px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const PricePeriod = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  flex: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  &::before {
    content: '\\2713';
    color: #10B981;
    font-weight: bold;
    font-size: 14px;
  }
`;

const LimitItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  &::before {
    content: '\\2022';
    color: ${({ theme }) => theme.colors.primary};
    font-size: 18px;
  }
`;

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ToggleOption = styled.button<{ $active: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.surface};
  color: ${({ $active }) => $active ? 'white' : 'inherit'};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  transition: all 0.15s;
`;

const SaveBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: #10B981;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

interface SubscriptionInfo {
  planId: string;
  plan?: SubscriptionPlan;
  planName?: string;
  monthlyPrice?: number;
  status: string;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
  isFreePlan?: boolean;
}

export function BillingPage() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [subscribing, setSubscribing] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [plansData, subData, usageData] = await Promise.all([
        apiClient.get<SubscriptionPlan[]>('/api/billing/plans'),
        apiClient.get<SubscriptionInfo>('/api/billing/subscription'),
        apiClient.get<UsageMetrics>('/api/billing/usage'),
      ]);
      setPlans(plansData);
      setSubscription(subData);
      setUsage(usageData);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscribe = async (planId: string) => {
    setSubscribing(true);
    try {
      await apiClient.post('/api/billing/subscribe', {
        planId,
        billingCycle,
      });
      fetchData();
    } catch (error) {
      console.error('Subscribe error:', error);
    } finally {
      setSubscribing(false);
    }
  };

  const handleCancel = async () => {
    try {
      await apiClient.post('/api/billing/cancel', {});
      setShowCancel(false);
      fetchData();
    } catch (error) {
      console.error('Cancel error:', error);
    }
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  const currentPlanId = subscription?.planId || 'free';

  return (
    <>
      <PageHeader>
        <Title>Billing & Subscription</Title>
        <Subtitle>Manage your plan and monitor usage</Subtitle>
      </PageHeader>

      {/* Current Plan */}
      <Section>
        <SectionTitle>Current Plan</SectionTitle>
        <CurrentPlanCard>
          <PlanInfo>
            <PlanName>
              {subscription?.plan?.name || subscription?.planName || 'Free'}
              <PlanStatus $active={subscription?.status === 'active'}>
                {subscription?.status === 'active' ? 'Active' : subscription?.status || 'Active'}
              </PlanStatus>
            </PlanName>
            <PlanPrice>
              {subscription?.isFreePlan || currentPlanId === 'free'
                ? 'Free forever'
                : `${formatPrice(subscription?.plan?.monthlyPrice || subscription?.monthlyPrice || 0)}/month`
              }
              {subscription?.cancelAtPeriodEnd && (
                <span style={{ color: '#EF4444', marginLeft: '8px' }}>
                  (Cancels at end of period)
                </span>
              )}
            </PlanPrice>
          </PlanInfo>
          {currentPlanId !== 'free' && !subscription?.cancelAtPeriodEnd && (
            <Button $variant="outline" onClick={() => setShowCancel(true)}
              style={{ color: '#EF4444', borderColor: '#EF4444' }}>
              Cancel Plan
            </Button>
          )}
        </CurrentPlanCard>
      </Section>

      {/* Usage */}
      {usage && (
        <Section>
          <SectionTitle>Current Usage</SectionTitle>
          <UsageGrid>
            <UsageCard>
              <UsageLabel>Posts Generated</UsageLabel>
              <UsageValue>{usage.postsGenerated} / {usage.postLimit}</UsageValue>
              <ProgressBar>
                <ProgressFill
                  $percent={(usage.postsGenerated / usage.postLimit) * 100}
                  $warning={usage.postsGenerated / usage.postLimit > 0.9}
                />
              </ProgressBar>
            </UsageCard>
            <UsageCard>
              <UsageLabel>Workspaces</UsageLabel>
              <UsageValue>{usage.workspacesCreated} / {usage.workspaceLimit}</UsageValue>
              <ProgressBar>
                <ProgressFill
                  $percent={(usage.workspacesCreated / usage.workspaceLimit) * 100}
                  $warning={usage.workspacesCreated / usage.workspaceLimit > 0.9}
                />
              </ProgressBar>
            </UsageCard>
            <UsageCard>
              <UsageLabel>Team Members</UsageLabel>
              <UsageValue>{usage.teamMembersAdded} / {usage.teamMemberLimit}</UsageValue>
              <ProgressBar>
                <ProgressFill
                  $percent={(usage.teamMembersAdded / usage.teamMemberLimit) * 100}
                  $warning={usage.teamMembersAdded / usage.teamMemberLimit > 0.9}
                />
              </ProgressBar>
            </UsageCard>
            {usage.reviewLimit > 0 && (
              <UsageCard>
                <UsageLabel>AI Reviews</UsageLabel>
                <UsageValue>
                  {usage.reviewsUsed} / {usage.reviewLimit === 9999 ? 'Unlimited' : usage.reviewLimit}
                </UsageValue>
                {usage.reviewLimit !== 9999 && (
                  <ProgressBar>
                    <ProgressFill
                      $percent={Math.min(100, (usage.reviewsUsed / usage.reviewLimit) * 100)}
                      $warning={usage.reviewsUsed / usage.reviewLimit > 0.9}
                    />
                  </ProgressBar>
                )}
              </UsageCard>
            )}
          </UsageGrid>
        </Section>
      )}

      {/* Available Plans */}
      <Section>
        <SectionTitle>Available Plans</SectionTitle>
        <BillingToggle>
          <ToggleOption $active={billingCycle === 'monthly'} onClick={() => setBillingCycle('monthly')}>
            Monthly
          </ToggleOption>
          <ToggleOption $active={billingCycle === 'annual'} onClick={() => setBillingCycle('annual')}>
            Annual <SaveBadge>Save 28%</SaveBadge>
          </ToggleOption>
        </BillingToggle>

        <PlansGrid>
          {plans.map(plan => {
            const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
            const monthlyEquiv = billingCycle === 'annual' ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice;
            const isCurrentPlan = plan.id === currentPlanId;
            const isPopular = plan.id === 'pro';

            return (
              <PlanCard key={plan.id} $highlighted={isPopular}>
                {isPopular && <PopularBadge>Most Popular</PopularBadge>}
                <PlanCardName>{plan.name}</PlanCardName>
                <PlanCardDesc>{plan.description}</PlanCardDesc>
                <PriceDisplay>
                  <PriceAmount>{price === 0 ? 'Free' : formatPrice(monthlyEquiv)}</PriceAmount>
                  {price > 0 && <PricePeriod>/month</PricePeriod>}
                  {billingCycle === 'annual' && price > 0 && (
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                      Billed {formatPrice(price)}/year
                    </div>
                  )}
                </PriceDisplay>

                <FeatureList>
                  <LimitItem>{plan.postLimit.toLocaleString()} posts/month</LimitItem>
                  <LimitItem>{plan.workspaceLimit} workspaces</LimitItem>
                  <LimitItem>{plan.teamMemberLimit} team member{plan.teamMemberLimit !== 1 ? 's' : ''}</LimitItem>
                  {plan.features.map((feature: string, i: number) => (
                    <FeatureItem key={i}>{feature}</FeatureItem>
                  ))}
                </FeatureList>

                <Button
                  $fullWidth
                  $variant={isCurrentPlan ? 'outline' : 'primary'}
                  disabled={isCurrentPlan || subscribing}
                  onClick={() => !isCurrentPlan && handleSubscribe(plan.id)}
                >
                  {isCurrentPlan ? 'Current Plan' : plan.id === 'free' ? 'Downgrade' : 'Upgrade'}
                </Button>
              </PlanCard>
            );
          })}
        </PlansGrid>
      </Section>

      {/* Cancel Confirmation */}
      <Modal
        isOpen={showCancel}
        onClose={() => setShowCancel(false)}
        title="Cancel Subscription"
        titleVariant="error"
        message="Are you sure you want to cancel? You'll still have access until the end of your current billing period."
      >
        <ModalActions>
          <Button $variant="outline" onClick={() => setShowCancel(false)}>Keep Plan</Button>
          <Button onClick={handleCancel} style={{ background: '#EF4444' }}>
            Confirm Cancel
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
